import React from "react";

/**
 * Shared display atoms for live-wind station tables (Cloudflare stations, Postgres legacy
 * stations, station management). One palette so a source / freshness reads the same on
 * every screen.
 */

export type Freshness = "live" | "stale" | "nodata";

/** undefined stale = no observation at all. */
export const freshnessOf = (stale: boolean | undefined, noData?: boolean): Freshness =>
  noData || stale === undefined ? "nodata" : stale ? "stale" : "live";

const SOURCE_STYLES: Record<string, string> = {
  push: "bg-blue-500/20 text-blue-300",
  metar: "bg-indigo-500/20 text-indigo-300",
  ndbc: "bg-cyan-500/20 text-cyan-300",
  knmi: "bg-emerald-500/20 text-emerald-300",
  pws: "bg-amber-500/20 text-amber-300",
  synthetic: "bg-purple-500/20 text-purple-300",
  tempest: "bg-pink-500/20 text-pink-300",
  clientraw: "bg-orange-500/20 text-orange-300",
  json: "bg-teal-500/20 text-teal-300",
};

export const SourceBadge: React.FC<{ source: string }> = ({ source }) => (
  <span
    className={`inline-block whitespace-nowrap rounded px-2 py-0.5 text-xs font-medium ${
      SOURCE_STYLES[source] || "bg-gray-600/40 text-gray-300"
    }`}
  >
    {source}
  </span>
);

const FRESHNESS: Record<Freshness, { label: string; cls: string }> = {
  live: { label: "live", cls: "bg-green-500/15 text-green-300" },
  stale: { label: "stale", cls: "bg-yellow-500/15 text-yellow-300" },
  nodata: { label: "no data", cls: "bg-gray-600/40 text-gray-300" },
};

export const FreshnessBadge: React.FC<{ freshness: Freshness }> = ({ freshness }) => (
  <span
    className={`inline-block whitespace-nowrap rounded px-2 py-0.5 text-xs font-medium ${FRESHNESS[freshness].cls}`}
  >
    {FRESHNESS[freshness].label}
  </span>
);

const STATUS_STYLES: Record<string, string> = {
  active: "bg-green-500/15 text-green-300",
  pending: "bg-yellow-500/15 text-yellow-300",
  revoked: "bg-red-500/15 text-red-300",
};

export const StationStatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <span
    className={`inline-block whitespace-nowrap rounded px-2 py-0.5 text-xs font-medium ${
      STATUS_STYLES[status] || "bg-gray-600/40 text-gray-300"
    }`}
  >
    {status}
  </span>
);

/** Relative age, e.g. "3 min ago"; "never" when no observation was ever ingested. */
export const fmtLastObs = (obsTs?: number | null): string => {
  if (!obsTs) return "never";
  const mins = Math.round((Date.now() - obsTs) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const h = Math.floor(mins / 60);
  if (h < 48) return `${h}h ${mins % 60}m ago`;
  return `${Math.floor(h / 24)}d ago`;
};

/** Knots with one decimal, "—" when absent. */
export const fmtKts = (v?: number | null): string => (v != null ? v.toFixed(1) : "—");
export const fmtDir = (v?: number | null): string => (v != null ? `${Math.round(v)}°` : "—");
